import { useMemo, useState } from "react";
import { Lock, Check, Sparkles, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { TEMPLATE_LIST, getTemplate, moodToTemplate } from "@/lib/portfolioTemplates";
import { toast } from "sonner";

/**
 * Mini preview card rendered with the template's own palette/font.
 * Click to select. Locked overlay if Premium-required and user isn't Premium.
 */
function TemplatePreviewCard({ tpl, selected, locked, onSelect }) {
  const v = tpl.vars;
  return (
    <button
      type="button"
      onClick={() => !locked && onSelect(tpl.id)}
      data-testid={`template-card-${tpl.id}`}
      className={`group relative text-left rounded-2xl overflow-hidden border transition-all duration-300 ${
        selected ? "ring-2 ring-offset-2 ring-[#C68E59] border-[#C68E59]" : "border-[#EADDC9] hover:-translate-y-0.5"
      } ${locked ? "cursor-not-allowed" : "cursor-pointer hover:shadow-lg"}`}
      style={{ background: v["--pt-bg"] }}
    >
      {/* Mini hero */}
      <div className="aspect-[4/3] relative" style={{ background: v["--pt-hero-bg"] }}>
        <div
          className="absolute inset-0 opacity-60"
          style={{
            backgroundImage: `radial-gradient(circle at 30% 20%, ${v["--pt-accent"]}33 0%, transparent 60%), radial-gradient(circle at 80% 80%, ${v["--pt-accent"]}22 0%, transparent 50%)`,
          }}
        />
        <div className="absolute inset-0 p-4 flex flex-col justify-end">
          <p
            className="text-[10px] tracking-[0.25em] uppercase"
            style={{ color: v["--pt-accent"], fontFamily: tpl.fonts.body }}
          >
            Portfolio
          </p>
          <p
            className="text-2xl leading-tight mt-1"
            style={{ color: v["--pt-hero-fg"], fontFamily: tpl.fonts.title }}
          >
            {tpl.name}
          </p>
          <div className="flex gap-1.5 mt-2">
            {[v["--pt-accent"], v["--pt-fg"], v["--pt-surface"]].map((c, i) => (
              <span key={i} className="w-3 h-3 rounded-full border border-black/10" style={{ background: c }} />
            ))}
          </div>
        </div>
        {selected && (
          <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-[#C68E59] text-white flex items-center justify-center shadow">
            <Check className="w-4 h-4" />
          </div>
        )}
        {locked && (
          <div className="absolute inset-0 bg-black/55 flex flex-col items-center justify-center text-white backdrop-blur-[1px]">
            <Lock className="w-5 h-5 mb-1.5" />
            <span className="text-[11px] tracking-widest uppercase">Premium</span>
          </div>
        )}
      </div>
      {/* Footer */}
      <div className="p-4 bg-white">
        <div className="flex items-center justify-between gap-2">
          <p className="font-serif text-base text-[#1A1A1A]">{tpl.name}</p>
          {tpl.premium ? (
            <span className="text-[10px] tracking-widest uppercase text-[#8B5A2B] bg-[#FAF3EA] px-2 py-0.5 rounded-full border border-[#E6C29A]">
              Premium
            </span>
          ) : (
            <span className="text-[10px] tracking-widest uppercase text-[#5C9B5C] bg-[#EFF7EE] px-2 py-0.5 rounded-full border border-[#C9E2C9]">
              Gratuit
            </span>
          )}
        </div>
        <p className="text-xs text-[#666] mt-1.5 leading-relaxed line-clamp-2">{tpl.description}</p>
      </div>
    </button>
  );
}

const UNIVERSE_OPTIONS = [
  { v: "minimal", label: "Minimal & doux" },
  { v: "luxe", label: "Luxueux & sombre" },
  { v: "street", label: "Streetwear & énergique" },
  { v: "artistique", label: "Artistique & créatif" },
  { v: "magazine", label: "Éditorial / Magazine" },
];
const AMBIANCE_OPTIONS = [
  { v: "clair", label: "Clair, lumineux" },
  { v: "sombre", label: "Sombre, profond" },
  { v: "contraste", label: "Contrasté, vif" },
  { v: "neutre", label: "Neutre, pastel" },
  { v: "colore", label: "Coloré, audacieux" },
];
const INSPIRATION_OPTIONS = [
  { v: "runway", label: "Runway / Haute couture" },
  { v: "magazine", label: "Magazine éditorial" },
  { v: "gallery", label: "Galerie d'art" },
  { v: "street", label: "Rue / Streetwear" },
  { v: "studio", label: "Studio minimal" },
];

export default function PortfolioTemplateSelector({ currentId, isPremium, onSelect }) {
  const [mood, setMood] = useState({ universe: "", ambiance: "", inspiration: "" });
  const recommendation = useMemo(() => {
    if (!mood.universe && !mood.ambiance && !mood.inspiration) return null;
    return moodToTemplate(mood);
  }, [mood]);
  const recTpl = recommendation ? getTemplate(recommendation) : null;

  const handleSelect = (id) => {
    const tpl = getTemplate(id);
    if (tpl.premium && !isPremium) {
      toast.error("Ce template est réservé aux membres Premium");
      return;
    }
    onSelect(id);
  };

  return (
    <div className="space-y-8">
      {/* ===== Mood Selector ===== */}
      <div className="bg-white rounded-2xl border border-[#EADDC9] p-7">
        <div className="flex items-center gap-2 mb-1.5">
          <Wand2 className="w-4 h-4 text-[#C68E59]" />
          <p className="caption-label text-[#C68E59]">Sélecteur d'ambiance</p>
        </div>
        <h3 className="font-serif text-xl text-[#1A1A1A]">Trouvez le style qui vous ressemble</h3>
        <p className="text-sm text-[#666] mt-1">
          Répondez à ces 3 questions, nous vous recommandons le template qui colle à votre univers.
        </p>

        <div className="grid sm:grid-cols-3 gap-4 mt-6">
          <div>
            <label className="text-[11px] tracking-widest uppercase text-[#8B8680]">Univers</label>
            <Select value={mood.universe} onValueChange={(v) => setMood({ ...mood, universe: v })}>
              <SelectTrigger className="mt-1.5 rounded-xl" data-testid="mood-universe">
                <SelectValue placeholder="Choisir…" />
              </SelectTrigger>
              <SelectContent>
                {UNIVERSE_OPTIONS.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] tracking-widest uppercase text-[#8B8680]">Ambiance visuelle</label>
            <Select value={mood.ambiance} onValueChange={(v) => setMood({ ...mood, ambiance: v })}>
              <SelectTrigger className="mt-1.5 rounded-xl" data-testid="mood-ambiance">
                <SelectValue placeholder="Choisir…" />
              </SelectTrigger>
              <SelectContent>
                {AMBIANCE_OPTIONS.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] tracking-widest uppercase text-[#8B8680]">Inspiration</label>
            <Select value={mood.inspiration} onValueChange={(v) => setMood({ ...mood, inspiration: v })}>
              <SelectTrigger className="mt-1.5 rounded-xl" data-testid="mood-inspiration">
                <SelectValue placeholder="Choisir…" />
              </SelectTrigger>
              <SelectContent>
                {INSPIRATION_OPTIONS.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {recTpl && (
          <div
            className="mt-6 rounded-2xl p-5 flex items-center gap-4 flex-wrap"
            style={{ background: recTpl.vars["--pt-accent-soft"], border: `1px solid ${recTpl.vars["--pt-accent"]}55` }}
            data-testid="mood-recommendation"
          >
            <Sparkles className="w-5 h-5" style={{ color: recTpl.vars["--pt-accent"] }} />
            <div className="flex-1 min-w-[200px]">
              <p className="text-[11px] tracking-widest uppercase" style={{ color: recTpl.vars["--pt-accent"] }}>
                Template recommandé
              </p>
              <p className="font-serif text-lg text-[#1A1A1A] mt-0.5">{recTpl.name}</p>
            </div>
            <Button
              onClick={() => handleSelect(recTpl.id)}
              className="rounded-full bg-[#1A1A1A] hover:bg-[#333] text-white"
              data-testid="apply-recommendation-btn"
              disabled={recTpl.premium && !isPremium}
            >
              {recTpl.premium && !isPremium ? "Premium requis" : "Appliquer"}
            </Button>
          </div>
        )}
      </div>

      {/* ===== Template Grid ===== */}
      <div>
        <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
          <div>
            <h3 className="font-serif text-2xl text-[#1A1A1A]">Tous les templates</h3>
            <p className="text-sm text-[#666] mt-1">
              {isPremium
                ? "Vous êtes Premium : tous les templates sont débloqués."
                : "2 templates inclus. 3 templates Premium pour aller plus loin."}
            </p>
          </div>
        </div>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {TEMPLATE_LIST.map((tpl) => (
            <TemplatePreviewCard
              key={tpl.id}
              tpl={tpl}
              selected={currentId === tpl.id}
              locked={tpl.premium && !isPremium}
              onSelect={handleSelect}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
